/**
 * Feature-edge line segments for extruded outlines — analytic, from the 2D outline
 * (cheap and exact for prisms; no mesh-angle scanning). Edge lines are computed from the
 * UNIONED group outline, so joined walls show clean single-outline edges while their
 * pickable meshes stay per-element (docs/bim-plan/01 §5, join model).
 */
import type { Pt2 } from '@aj/bim-kernel'

/**
 * Feature edges from a TRIANGLE MESH (r10 fix — Matt's report: a base cabinet showed
 * "just two top lines" in elevation because the casework proto hand-curated only its
 * top-face outline). Boundary edges plus any edge whose two faces bend past the
 * dihedral threshold. Vertices key by QUANTIZED POSITION, not index — flat-shaded
 * builders duplicate vertices per face, so index-keyed matching would call every
 * edge a boundary. This is the DETAIL-PROOF mechanism: as prototypes gain panels,
 * hardware, and shelving, their linework derives from the mesh automatically —
 * nothing hand-curated to forget. Coplanar tessellation diagonals drop out (0°);
 * smooth tessellated arcs stay clean (soft edges below threshold don't draw) while
 * their profile silhouettes survive (face-vs-side is ~90°).
 */
export function featureEdges(
  mesh: { positions: Float32Array; indices: Uint32Array },
  angleDeg = 25,
): Float32Array {
  const pos = mesh.positions, idx = mesh.indices
  const cosThresh = Math.cos((angleDeg * Math.PI) / 180)
  const q = (v: number) => Math.round(v * 2048)
  const vKey = (i: number) => `${q(pos[i * 3])},${q(pos[i * 3 + 1])},${q(pos[i * 3 + 2])}`
  interface EdgeRec { a: number; b: number; normals: [number, number, number][] }
  const map = new Map<string, EdgeRec>()
  for (let t = 0; t < idx.length; t += 3) {
    const i0 = idx[t], i1 = idx[t + 1], i2 = idx[t + 2]
    // face normal from positions (vertex normals are per-face duplicates anyway)
    const ax = pos[i1 * 3] - pos[i0 * 3], ay = pos[i1 * 3 + 1] - pos[i0 * 3 + 1], az = pos[i1 * 3 + 2] - pos[i0 * 3 + 2]
    const bx = pos[i2 * 3] - pos[i0 * 3], by = pos[i2 * 3 + 1] - pos[i0 * 3 + 1], bz = pos[i2 * 3 + 2] - pos[i0 * 3 + 2]
    let nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx
    const nl = Math.hypot(nx, ny, nz)
    if (nl < 1e-12) continue
    nx /= nl; ny /= nl; nz /= nl
    for (const [va, vb] of [[i0, i1], [i1, i2], [i2, i0]] as const) {
      const ka = vKey(va), kb = vKey(vb)
      if (ka === kb) continue
      const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`
      let rec = map.get(key)
      if (!rec) { rec = { a: va, b: vb, normals: [] }; map.set(key, rec) }
      rec.normals.push([nx, ny, nz])
    }
  }
  const seg: number[] = []
  for (const rec of map.values()) {
    let hard = rec.normals.length === 1   // boundary
    if (!hard) {
      // hard when ANY face pair bends past the threshold
      outer: for (let i = 0; i < rec.normals.length; i++) {
        for (let j = i + 1; j < rec.normals.length; j++) {
          const [x1, y1, z1] = rec.normals[i], [x2, y2, z2] = rec.normals[j]
          if (x1 * x2 + y1 * y2 + z1 * z2 < cosThresh) { hard = true; break outer }
        }
      }
    }
    if (!hard) continue
    seg.push(
      pos[rec.a * 3], pos[rec.a * 3 + 1], pos[rec.a * 3 + 2],
      pos[rec.b * 3], pos[rec.b * 3 + 1], pos[rec.b * 3 + 2],
    )
  }
  return new Float32Array(seg)
}

/**
 * Angle tolerance for "this vertex doesn't turn": Clipper2's integer grid is 1e-4 ft,
 * so a genuinely-straight junction vertex can deviate by a few 1e-4 ft over short
 * segments — ~0.57° (sin 0.01) absorbs that with 50× margin while never touching a
 * real corner (even a 1° wall kink stays a drawn edge... barely; real corners are ≫).
 */
const STRAIGHT_SIN_TOL = 0.01

/**
 * FOUNDATION CORNER + STEP round (2026-08-16): a vertical at `p` is suppressed only
 * over [z0, z1] — the sub-band where an abutting solid continues the surface — and
 * the REAL remainder (a stepped stem's riser, a stepped footing's exposed riser)
 * still draws. The point-only `excludeAt` stays for full-band suppression.
 */
export interface VerticalRangeExclusion { p: Pt2; z0: number; z1: number }

/**
 * FOUNDATION CORNER + STEP round (2026-08-16): a top/bottom ring SEGMENT portion at
 * height `z` lying collinear over a–b is suppressed — the stepped-footing lap case,
 * where a ring runs along a face that another footing piece continues past.
 */
export interface RingSegExclusion { z: number; a: Pt2; b: Pt2 }

/** subtract [lo, hi] from a sorted, disjoint interval list (in place semantics via return) */
function subtractInterval(ivs: [number, number][], lo: number, hi: number): [number, number][] {
  const out: [number, number][] = []
  for (const [a, b] of ivs) {
    if (hi <= a + 1e-9 || lo >= b - 1e-9) { out.push([a, b]); continue }
    if (lo > a + 1e-9) out.push([a, Math.min(lo, b)])
    if (hi < b - 1e-9) out.push([Math.max(hi, a), b])
  }
  return out
}

/**
 * Vertical edges at every outline vertex where the outline actually TURNS, plus top
 * and bottom perimeter rings. The turn test matters (2026-07-31, Matt's unchained-
 * corner report): Clipper2 boolean output PRESERVES collinear vertices, so the unioned
 * group outline keeps a vertex where a corner-weld patch met each wall's face — a
 * vertical there drew a phantom full-height seam on a perfectly flat face.
 */
export function prismEdges(
  outlines: Pt2[][],
  zBase: number,
  zTop: number,
  // ATTACH-TARGETS/CURB round (2026-08-10): a derived mesh skin continuing past the
  // prism's top/bottom means that ring is NOT a silhouette line — the skin draws its
  // own outlines instead (an attached wall must read as ONE wall, not a wall with a
  // horizontal line at its old height)
  opts?: {
    skipTopRing?: boolean
    skipBottomRing?: boolean
    /**
     * ATTACH CORNER round follow-up (2026-08-12, Matt's screenshot: a doubled
     * vertical surviving right at an attached corner even after the skin-mesh
     * fix): world points where the outline geometrically turns but a resolved
     * 2-wall corner weld continues the silhouette straight through it (the
     * notch, or — for a flat corner whose two walls don't share this chunk's
     * groupKey — a plain union that would have absorbed it). A flat corner
     * that DOES share this group's outline never has a vertex here at all
     * (unionClosed already erased it), so this is a no-op there.
     */
    excludeAt?: Pt2[]
    /** z-RANGED vertical suppression (FOUNDATION CORNER + STEP round) — the
     *  vertical at a matching point draws only OUTSIDE the excluded band(s), so a
     *  step riser keeps its real edge while the continuous run above loses the
     *  phantom one. Ranges clamp to this prism's own [zBase, zTop]. */
    excludeVerticalRanges?: VerticalRangeExclusion[]
    /** ring-SEGMENT suppression (FOUNDATION CORNER + STEP round) — top/bottom
     *  ring portions collinear with a matching exclusion segment at that ring's
     *  z are dropped (the stepped-footing lap's buried seam lines). */
    excludeRingSegs?: RingSegExclusion[]
  },
): Float32Array {
  const seg: number[] = []
  const excl = opts?.excludeAt
  const exclRanges = opts?.excludeVerticalRanges
  const exclRings = opts?.excludeRingSegs
  const pushRingSeg = (p: Pt2, q: Pt2, z: number) => {
    if (exclRings?.length) {
      const dx = q[0] - p[0], dy = q[1] - p[1]
      const L = Math.hypot(dx, dy)
      if (L < 1e-9) return
      const ux = dx / L, uy = dy / L
      let ivs: [number, number][] = [[0, L]]
      for (const ex of exclRings) {
        if (Math.abs(ex.z - z) > 1e-3) continue
        // both exclusion endpoints must lie on this segment's line (collinear)
        const perpA = Math.abs(ux * (ex.a[1] - p[1]) - uy * (ex.a[0] - p[0]))
        const perpB = Math.abs(ux * (ex.b[1] - p[1]) - uy * (ex.b[0] - p[0]))
        if (perpA > 1e-3 || perpB > 1e-3) continue
        const ta = (ex.a[0] - p[0]) * ux + (ex.a[1] - p[1]) * uy
        const tb = (ex.b[0] - p[0]) * ux + (ex.b[1] - p[1]) * uy
        const lo = Math.max(0, Math.min(ta, tb)), hi = Math.min(L, Math.max(ta, tb))
        if (hi - lo < 1e-6) continue
        ivs = subtractInterval(ivs, lo, hi)
        if (!ivs.length) return
      }
      for (const [t0, t1] of ivs) {
        if (t1 - t0 < 1e-6) continue
        seg.push(p[0] + ux * t0, p[1] + uy * t0, z, p[0] + ux * t1, p[1] + uy * t1, z)
      }
      return
    }
    seg.push(p[0], p[1], z, q[0], q[1], z)
  }
  for (const ring of outlines) {
    const n = ring.length
    for (let i = 0; i < n; i++) {
      const o = ring[(i + n - 1) % n], p = ring[i], q = ring[(i + 1) % n]
      const d1x = p[0] - o[0], d1y = p[1] - o[1]
      const d2x = q[0] - p[0], d2y = q[1] - p[1]
      const cross = d1x * d2y - d1y * d2x
      const dot = d1x * d2x + d1y * d2y
      const straight = dot > 0 &&
        Math.abs(cross) < STRAIGHT_SIN_TOL * Math.hypot(d1x, d1y) * Math.hypot(d2x, d2y)
      const excluded = !straight && (excl?.some(([ex, ey]) => Math.hypot(ex - p[0], ey - p[1]) < 1e-3) ?? false)
      // vertical at vertex p — only at a true, non-excluded corner; z-ranged
      // exclusions subtract their band and the remainder(s) still draw
      if (!straight && !excluded) {
        let ivs: [number, number][] = [[zBase, zTop]]
        if (exclRanges?.length) {
          for (const ex of exclRanges) {
            if (Math.hypot(ex.p[0] - p[0], ex.p[1] - p[1]) >= 1e-3) continue
            ivs = subtractInterval(ivs, ex.z0, ex.z1)
            if (!ivs.length) break
          }
        }
        for (const [z0, z1] of ivs) {
          if (z1 - z0 < 1e-6) continue
          seg.push(p[0], p[1], z0, p[0], p[1], z1)
        }
      }
      // top + bottom perimeter segments p->q
      if (!opts?.skipTopRing) pushRingSeg(p, q, zTop)
      if (!opts?.skipBottomRing) pushRingSeg(p, q, zBase)
    }
  }
  return new Float32Array(seg)
}
